import { useCallback, useEffect, useState } from 'react';
import { cancelSchedule, createSchedule, fetchSchedules, fetchSavedRooms } from './api';
import type { SavedRoom, Schedule as Booked } from './api';
import type { Route } from './routes';

/**
 * Meetings that have not happened yet.
 *
 * THE OPEN QUESTION FROM THE CALL was whether to build a calendar into the
 * product or lean on the emailed `.ics`. This is the second answer, and the
 * reason is that Google Meet's leverage is that it IS Google Calendar — a
 * position we do not have and would not gain by building a second diary nobody
 * checks. The `.ics` is the interop point every calendar already understands,
 * so the invitation lands in the one people actually keep.
 *
 * What this page is, then, is not a calendar: it is the LIST of what was sent
 * and what is coming, which is a different and smaller thing. No month grid, no
 * drag-to-create, nothing that competes with the calendar already open in the
 * next tab.
 */

/** The browser's own zone. A series is anchored to a named zone, not an offset. */
const localZone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};

/** `datetime-local` gives "2026-09-22T16:00"; the API wants an offset. */
function toRFC3339(local: string): string {
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString();
}

/** Default to the next round half-hour, which is what somebody usually means. */
function defaultStart(): string {
  const d = new Date(Date.now() + 60 * 60 * 1000);
  d.setMinutes(d.getMinutes() > 30 ? 60 : 30, 0, 0);
  // Back to local wall-clock for the input, which has no notion of zones.
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function when(iso: string, tzid: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: tzid,
      timeZoneName: 'short',
    });
  } catch {
    return new Date(iso).toLocaleString();
  }
}

const DAYS: { code: string; label: string }[] = [
  { code: 'MO', label: 'Mon' },
  { code: 'TU', label: 'Tue' },
  { code: 'WE', label: 'Wed' },
  { code: 'TH', label: 'Thu' },
  { code: 'FR', label: 'Fri' },
  { code: 'SA', label: 'Sat' },
  { code: 'SU', label: 'Sun' },
];

/**
 * The recurrence in words.
 *
 * The RRULE is for the recipient's calendar; nobody should have to read
 * "FREQ=WEEKLY;BYDAY=TU" to find out when their meeting is.
 */
function repeats(rrule?: string): string {
  if (!rrule) return 'once';
  const parts: Record<string, string> = {};
  for (const p of rrule.split(';')) {
    const [k, v] = p.split('=');
    if (k) parts[k] = v ?? '';
  }
  const every = Number(parts.INTERVAL ?? 1);
  const n = every > 1 ? ` ${every}` : '';
  switch (parts.FREQ) {
    case 'DAILY':
      return every > 1 ? `every${n} days` : 'daily';
    case 'WEEKLY': {
      const days = (parts.BYDAY ?? '')
        .split(',')
        .map((c) => DAYS.find((d) => d.code === c)?.label)
        .filter(Boolean);
      const base = every > 1 ? `every${n} weeks` : 'weekly';
      return days.length ? `${base} on ${days.join(', ')}` : base;
    }
    case 'MONTHLY':
      return every > 1 ? `every${n} months` : 'monthly';
    default:
      return 'once';
  }
}

function Booking({ onBooked }: { onBooked: () => void }) {
  const [open, setOpen] = useState(false);
  const [rooms, setRooms] = useState<SavedRoom[]>([]);
  const [form, setForm] = useState({
    title: '',
    start: defaultStart(),
    minutes: 30,
    freq: '' as '' | 'DAILY' | 'WEEKLY' | 'MONTHLY',
    interval: 1,
    byday: [] as string[],
    invitees: '',
    remind: 30,
    room: '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    // Offered so a standing meeting reuses its room — which is what makes "the
    // usual link" mean something. A failure here is not worth an error: the
    // field simply offers nothing and a room gets minted instead.
    fetchSavedRooms()
      .then(setRooms)
      .catch(() => setRooms([]));
  }, [open]);

  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const book = () => {
    const start = toRFC3339(form.start);
    if (!start) {
      setError('That is not a date I can read.');
      return;
    }
    setBusy(true);
    setError(null);
    createSchedule({
      title: form.title.trim(),
      start,
      minutes: form.minutes,
      tzid: localZone(),
      freq: form.freq,
      interval: form.interval,
      // Weekly with no day chosen means "the day the first one falls on",
      // which the server works out — so an empty list is a valid answer.
      byday: form.freq === 'WEEKLY' ? form.byday.join(',') : '',
      invitees: form.invitees,
      remind: form.remind,
      room: form.room || undefined,
    })
      .then(() => {
        setOpen(false);
        setForm((f) => ({ ...f, title: '', invitees: '' }));
        onBooked();
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setBusy(false));
  };

  if (!open) {
    return (
      <section className="mx-room-card mx-start">
        <div className="mx-room-head">
          <h2>Schedule a meeting</h2>
        </div>
        <p className="mx-muted">
          For something that is not happening right now. Everyone on the list gets a calendar
          invitation with the join link in it, and a reminder before it starts.
        </p>
        <div className="mx-room-actions">
          <button className="mx-button mx-button-inline" onClick={() => setOpen(true)}>
            Schedule a meeting
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="mx-room-card mx-start">
      <div className="mx-room-head">
        <h2>Schedule a meeting</h2>
      </div>
      <form
        className="mx-start-form"
        onSubmit={(e) => {
          e.preventDefault();
          book();
        }}
      >
        <label>
          What is it about?
          <input
            autoFocus
            value={form.title}
            onChange={(e) => set('title', e.target.value)}
            placeholder="Weekly with Roger"
            maxLength={120}
          />
        </label>

        <div className="mx-field-row">
          <label>
            When
            <input
              type="datetime-local"
              value={form.start}
              onChange={(e) => set('start', e.target.value)}
            />
          </label>
          <label>
            For
            <select
              value={form.minutes}
              onChange={(e) => set('minutes', Number(e.target.value))}
            >
              {[15, 30, 45, 60, 90, 120].map((m) => (
                <option key={m} value={m}>
                  {m < 60 ? `${m} min` : `${m / 60} hour${m > 60 ? 's' : ''}`}
                </option>
              ))}
            </select>
          </label>
          <label>
            Repeats
            <select
              value={form.freq}
              onChange={(e) => set('freq', e.target.value as typeof form.freq)}
            >
              <option value="">Once</option>
              <option value="DAILY">Daily</option>
              <option value="WEEKLY">Weekly</option>
              <option value="MONTHLY">Monthly</option>
            </select>
          </label>
        </div>

        {form.freq === 'WEEKLY' && (
          <div className="mx-days">
            {DAYS.map((d) => {
              const on = form.byday.includes(d.code);
              return (
                <button
                  type="button"
                  key={d.code}
                  className={`mx-chip${on ? ' on' : ''}`}
                  aria-pressed={on}
                  onClick={() =>
                    set(
                      'byday',
                      on ? form.byday.filter((c) => c !== d.code) : [...form.byday, d.code],
                    )
                  }
                >
                  {d.label}
                </button>
              );
            })}
            <span className="mx-muted">
              {form.byday.length === 0 && 'the same day each week'}
            </span>
          </div>
        )}

        <label>
          Invite (email addresses, comma or newline separated)
          <textarea
            rows={2}
            value={form.invitees}
            onChange={(e) => set('invitees', e.target.value)}
            placeholder="roger@example.com, dana@example.com"
          />
        </label>

        <div className="mx-field-row">
          <label>
            Remind them
            <select value={form.remind} onChange={(e) => set('remind', Number(e.target.value))}>
              <option value={0}>No reminder</option>
              <option value={15}>15 min before</option>
              <option value={30}>30 min before</option>
              <option value={60}>1 hour before</option>
            </select>
          </label>
          {rooms.length > 0 && (
            <label>
              In
              <select value={form.room} onChange={(e) => set('room', e.target.value)}>
                <option value="">A new room</option>
                {rooms.map((r) => (
                  <option key={r.name} value={r.name}>
                    {r.title}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>

        <div className="mx-room-actions">
          <button
            className="mx-button mx-button-inline"
            type="submit"
            disabled={busy || !form.title.trim()}
          >
            {busy ? 'Sending…' : 'Schedule and invite'}
          </button>
          <button className="mx-invite-btn" type="button" onClick={() => setOpen(false)}>
            Cancel
          </button>
        </div>
      </form>

      {error && <p className="mx-error">{error}</p>}
      <p className="mx-muted">
        Times are in {localZone()}. A repeating meeting keeps its wall-clock time through a
        daylight-saving change — 4pm stays 4pm.
      </p>
    </section>
  );
}

function Upcoming({ item, onChanged }: { item: Booked; onChanged: () => void }) {
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const start = item.nextStart ?? item.startsAt;

  return (
    <section className="mx-room-card">
      <div className="mx-room-head">
        <h2>{item.title}</h2>
        {item.rrule && <span className="mx-pill">{repeats(item.rrule)}</span>}
      </div>

      <p className="mx-room-who">
        {when(start, item.tzid)}
        <span className="mx-muted">
          {' '}
          · {item.minutes} min
          {item.remindMinutes > 0 && ` · reminder ${item.remindMinutes} min before`}
        </span>
      </p>

      {item.invitees.length > 0 && (
        <p className="mx-muted">Invited: {item.invitees.join(', ')}</p>
      )}

      {error && <p className="mx-error">{error}</p>}

      <div className="mx-room-actions">
        {item.joinUrl && (
          <>
            <a className="mx-button mx-button-inline" href={item.joinUrl}>
              Join
            </a>
            {/* The link is what somebody actually needs to forward — an invite
                that bounced, a person added after the fact. Shown as well as
                copied, because a clipboard write fails silently in plenty of
                browsers. */}
            <button
              className="mx-invite-btn"
              onClick={() => {
                navigator.clipboard
                  .writeText(item.joinUrl!)
                  .then(() => setCopied(true))
                  .catch(() => setCopied(false));
              }}
            >
              {copied ? 'Link copied' : 'Copy link'}
            </button>
          </>
        )}
        <button
          className="mx-invite-btn"
          onClick={() =>
            cancelSchedule(item.id)
              .then(onChanged)
              .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)))
          }
        >
          Cancel
        </button>
      </div>

      {item.joinUrl && <p className="mx-muted mx-joinurl">{item.joinUrl}</p>}
    </section>
  );
}

export function Schedule({ navigate }: { navigate: (r: Route) => void }) {
  const [list, setList] = useState<Booked[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    fetchSchedules()
      .then(setList)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  useEffect(load, [load]);

  return (
    <>
      <Booking onBooked={load} />

      {error && <p className="mx-error">{error}</p>}
      {!list ? (
        <p className="mx-muted">Loading…</p>
      ) : list.length === 0 ? (
        <p className="mx-muted">
          Nothing scheduled. A meeting booked here sends everyone a calendar invitation with the
          join link in it — so it turns up in the calendar they already keep.
        </p>
      ) : (
        list.map((s) => <Upcoming key={s.id} item={s} onChanged={load} />)
      )}

      <nav className="mx-dash-links">
        <button className="mx-back" onClick={() => navigate({ name: 'rooms' })}>
          Rooms and who can read them
        </button>
        <button className="mx-back" onClick={() => navigate({ name: 'dashboard' })}>
          Start a meeting now
        </button>
      </nav>
    </>
  );
}
